import React from "react";
import { SEO } from "./SEO";

// Helper function to treat null/undefined/empty string as missing
const safeValue = (value: any): any => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return value;
};

// Define interface for product attribute values
interface ProductAttributeValue {
  name: string;
  colorHex?: string | null;
}

// Define interface for product attributes
interface ProductAttribute {
  name: string;
  uniqueName: string;
  isGroupingAttribute?: boolean;
  groupingAttributeStyle?: string | null;
  values?: ProductAttributeValue[];
}

// Define interface for structured data schema
interface StructuredDataSchema {
  "@context": string;
  "@type": string;
  name: string;
  description: string;
  sku: string;
  url: string;
  image?: string[];
  brand: {
    "@type": string;
    name: string;
  };
  category?: string;
  offers: {
    "@type": string;
    price: string;
    priceCurrency: string;
    availability: string;
    priceValidUntil: string;
    itemCondition: string;
    seller: {
      "@type": string;
      name: string;
      url: string;
    };
    url: string;
    shippingDetails?: {
      "@type": string;
      shippingDestination: {
        "@type": string;
        addressCountry: string;
      };
      deliveryTime: {
        "@type": string;
        handlingTime: {
          "@type": string;
          minValue: number;
          maxValue: number;
          unitCode: string;
        };
        transitTime: {
          "@type": string;
          minValue: number;
          maxValue: number;
          unitCode: string;
        };
      };
      shippingRate: {
        "@type": string;
        currency: string;
        value: string;
      };
    };
  };
  additionalProperty?: Array<{
    "@type": string;
    name: string;
    value: string;
  }>;
  mpn?: string;
  gtin?: string;
  google_product_category?: string;
  hasMerchantReturnPolicy?: {
    "@type": string;
    applicableCountry: string;
    returnPolicyCategory: string;
    merchantReturnDays: number;
    returnMethod: string;
    returnFees: string;
    url: string;
  };
  [key: string]: any; // Allow additional properties
}

interface ProductSEOProps {
  product: any; // Update to use the specific product type from your API
}

export const ProductSEO: React.FC<ProductSEOProps> = ({ product }) => {
  // Add validation to ensure we have a valid product
  if (!product || !product.name) {
    return null;
  }

  const currentProduct = product; // Assuming product data is now clean from backend

  // Extract price information - handle both old discount system and new calculatedDiscount
  const regPrice = Number(currentProduct.price?.regPrice || 0);

  let discountPrice: number | null = null;
  let discountEndDate: string | null = null;

  if (currentProduct.calculatedDiscount?.discountedPrice) {
    discountPrice = Number(currentProduct.calculatedDiscount.discountedPrice);
    discountEndDate = currentProduct.calculatedDiscount.endDatetime
      ? String(currentProduct.calculatedDiscount.endDatetime)
      : null;
  } else if (currentProduct.price?.discount?.newPrice) {
    discountPrice = Number(currentProduct.price.discount.newPrice);
    discountEndDate = currentProduct.price.discount.endDatetime
      ? String(currentProduct.price.discount.endDatetime)
      : null;
  }

  const finalPrice = discountPrice !== null ? discountPrice : regPrice;
  const hasDiscount = discountPrice !== null && discountPrice < regPrice;

  // Extract other product information
  const productName = String(currentProduct.name || "");
  const productDescription = String(
    currentProduct.description || `${productName} - Disponible en Evefant`
  );
  const productSku = String(currentProduct.sku || "");
  const partnerName = String(currentProduct.partner?.name || "Evefant");
  const stockNumber = Number(currentProduct.stock || 0);

  // Extract image URL
  const imageUrl = String(
    currentProduct.images?.[0]?.medium ||
      currentProduct.images?.[0]?.small ||
      ""
  );

  // Handle price valid until date
  const priceValidUntil = (() => {
    if (hasDiscount && discountEndDate) {
      return discountEndDate;
    }
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().split("T")[0];
  })();

  // Get current page URL
  const currentUrl = String(
    currentProduct.seo?.canonicalURL ||
      (typeof window !== "undefined" ? window.location.href : "")
  );

  // Function to generate fallback structured data
  const generateFallbackStructuredData = (): StructuredDataSchema => {
    // Build additional properties from product attributes
    const additionalProperties: Array<{
      "@type": string;
      name: string;
      value: string;
    }> = [];

    if (currentProduct.attributes && Array.isArray(currentProduct.attributes)) {
      currentProduct.attributes.forEach((attr: ProductAttribute) => {
        if (attr.values && Array.isArray(attr.values)) {
          attr.values.forEach((val: ProductAttributeValue) => {
            if (val.name) {
              additionalProperties.push({
                "@type": "PropertyValue",
                name: attr.name || attr.uniqueName,
                value: val.name,
              });
            }
          });
        }
      });
    }

    // Determine availability for configurable products
    let availability = "https://schema.org/OutOfStock";
    if (
      currentProduct.hasChildren &&
      currentProduct.children &&
      Array.isArray(currentProduct.children)
    ) {
      // For configurable products, check if any children have stock
      const hasStockInChildren = currentProduct.children.some(
        (child: any) => child.stock && child.stock > 0
      );
      availability = hasStockInChildren
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock";
    } else {
      // For simple products, check direct stock
      availability =
        stockNumber > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock";
    }

    // Build image array with fallback to default placeholder
    const images: string[] = [];
    if (currentProduct.images && Array.isArray(currentProduct.images)) {
      currentProduct.images.forEach((image: any) => {
        // Handle the actual data structure: { small, medium, thumbnail }
        const imageUrl =
          image?.medium || image?.small || image?.thumbnail || image?.url;
        if (imageUrl) {
          // Images are already absolute URLs from media.evefant.com
          images.push(imageUrl);
        }
      });
    }

    // Ensure at least one image for Google Rich Results compliance
    if (images.length === 0) {
      images.push("https://evefant.com/favicon.png"); // Fallback to site logo
    }

    // Get category name
    const categoryName =
      currentProduct.product_categories &&
      Array.isArray(currentProduct.product_categories) &&
      currentProduct.product_categories.length > 0
        ? currentProduct.product_categories[0].name
        : undefined;

    const fallbackSchema: StructuredDataSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: productName,
      description: productDescription,
      sku: productSku,
      url: currentUrl,
      image: images, // Always provide images array (with fallback if needed)
      brand: {
        "@type": "Brand",
        name: partnerName,
      },
      category: categoryName,
      offers: {
        "@type": "Offer",
        price: finalPrice.toString(),
        priceCurrency: "PEN",
        availability: availability,
        priceValidUntil: priceValidUntil,
        itemCondition: "https://schema.org/NewCondition",
        seller: {
          "@type": "Organization",
          name: partnerName,
          url: "https://evefant.com",
        },
        url: currentUrl,
        // Google Merchant Center shipping details
        shippingDetails: {
          "@type": "OfferShippingDetails",
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "PE",
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: {
              "@type": "QuantitativeValue",
              minValue: 1,
              maxValue: 3,
              unitCode: "DAY",
            },
            transitTime: {
              "@type": "QuantitativeValue",
              minValue: 1,
              maxValue: 7,
              unitCode: "DAY",
            },
          },
          shippingRate: {
            "@type": "MonetaryAmount",
            currency: "PEN",
            value: "Variable", // Indicates shipping cost varies by location/weight
          },
        },
      },
      // Google Merchant Center return policy
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "PE",
        returnPolicyCategory:
          "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 1,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
        url: "https://evefant.com/legales/politica-cambios-devoluciones",
      },
    };

    // Add additional properties if we have them
    if (additionalProperties.length > 0) {
      fallbackSchema.additionalProperty = additionalProperties;
    }

    // Add MPN if available
    if (currentProduct.partnerSku) {
      fallbackSchema.mpn = currentProduct.partnerSku;
    }

    // Add GTIN using SKU if available
    if (productSku) {
      fallbackSchema.gtin = productSku;
    }

    // Add Google Product Category for better classification
    if (categoryName) {
      fallbackSchema.google_product_category = categoryName;
    }

    // Remove undefined properties
    Object.keys(fallbackSchema).forEach((key) => {
      if (fallbackSchema[key] === undefined) {
        delete fallbackSchema[key];
      }
    });

    return fallbackSchema;
  };

  // Check if backend structured data exists and is valid
  let productSchema: StructuredDataSchema | null = null;

  try {
    // Prioritize new enhanced structured data from backend service over legacy seo.structuredData
    const backendStructuredData =
      safeValue(currentProduct.structuredData) ||
      safeValue(currentProduct.seo?.structuredData);

    if (backendStructuredData) {
      // If it's a string, try to parse it
      if (typeof backendStructuredData === "string") {
        productSchema = JSON.parse(backendStructuredData);
      } else if (typeof backendStructuredData === "object") {
        productSchema = backendStructuredData;
      }

      // Validate that we have essential fields before using backend data
      if (
        productSchema &&
        productSchema["@type"] &&
        productSchema.name &&
        productSchema.offers
      ) {
        // Backend data is complete, no need for fallback
        console.log("Using backend structured data for product:", productName);
      } else {
        // Backend data is incomplete, generate fallback
        console.warn(
          "Backend structured data incomplete, using fallback for product:",
          productName
        );
        productSchema = generateFallbackStructuredData();
      }
    }
  } catch (error) {
    console.warn(
      "Invalid structured data from backend, using fallback:",
      error
    );
    productSchema = null;
  }

  // Only generate fallback if no valid backend structured data exists
  if (!productSchema) {
    console.log(
      "No backend structured data, generating fallback for product:",
      productName
    );
    productSchema = generateFallbackStructuredData();
  }

  // Extract attributes for meta keywords
  const attributeValues =
    currentProduct.attributes
      ?.flatMap(
        (attr: ProductAttribute) =>
          attr.values?.map((val: ProductAttributeValue) => val.name) || []
      )
      .filter(Boolean) || [];

  const keywords = [productName, ...attributeValues, partnerName]
    .filter(Boolean)
    .join(", ");

  // Use SEO data from API if available, otherwise generate from product data
  // Treat null values as missing data
  const seoTitle = String(
    safeValue(currentProduct.seo?.metaTitle) || productName
  );
  const seoDescription = String(
    safeValue(currentProduct.seo?.metaDescription) ||
      safeValue(currentProduct.description?.substring(0, 160)) ||
      `Compra ${productName} en Evefant`
  );
  const seoKeywords = String(
    safeValue(currentProduct.seo?.keywords) || keywords
  );
  const canonicalURL = String(
    safeValue(currentProduct.seo?.canonicalURL) ||
      `https://evefant.com/p/${currentProduct.url}`
  );

  // Handle OpenGraph with null safety
  const openGraph = safeValue(currentProduct.seo?.openGraph) || null;

  // Debug logging to understand the data structure
  console.log("🔍 ProductSEO Debug - SEO data structure:", {
    hasSEO: !!currentProduct.seo,
    seoKeys: currentProduct.seo ? Object.keys(currentProduct.seo) : [],
    hasOpenGraph: !!currentProduct.seo?.openGraph,
    openGraphData: currentProduct.seo?.openGraph,
    metaTitle: currentProduct.seo?.metaTitle,
    metaDescription: currentProduct.seo?.metaDescription,
  });

  const seoData = currentProduct.seo || {};

  const finalImageUrl = String(
    safeValue(openGraph?.["og:image"]?.url) ||
      safeValue(openGraph?.["og:image"]) ||
      safeValue(openGraph?.image?.url) ||
      safeValue(openGraph?.image) ||
      safeValue(seoData?.metaImage?.url) ||
      safeValue(seoData?.metaImage) ||
      imageUrl
  );

  const finalOgType = String(safeValue(openGraph?.["og:type"]) || "product");

  const finalSEOProps = {
    title: seoTitle,
    description: seoDescription,
    image: finalImageUrl,
    type: finalOgType,
    schema: productSchema,
    openGraph: openGraph,
    seoData: seoData,
    author: partnerName,
    publisher: "Evefant",
  };

  // Debug logging for final props
  console.log("🔍 ProductSEO Debug - Final props being passed to SEO:", {
    title: finalSEOProps.title,
    description: finalSEOProps.description,
    image: finalSEOProps.image,
    type: finalSEOProps.type,
    hasOpenGraph: !!finalSEOProps.openGraph,
    openGraphKeys: finalSEOProps.openGraph
      ? Object.keys(finalSEOProps.openGraph)
      : [],
    hasSeoData: !!finalSEOProps.seoData,
    seoDataKeys: finalSEOProps.seoData
      ? Object.keys(finalSEOProps.seoData)
      : [],
  });

  return (
    <SEO
      title={finalSEOProps.title}
      description={finalSEOProps.description}
      image={finalSEOProps.image}
      type={finalSEOProps.type}
      schema={finalSEOProps.schema}
      openGraph={finalSEOProps.openGraph}
      seoData={finalSEOProps.seoData}
      author={finalSEOProps.author}
      publisher={finalSEOProps.publisher}
      useGlobalFallbacks={true} // Enable global SEO fallbacks
    >
      {/* Additional meta tags for SEO */}
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content={finalSEOProps.author} />
      <meta name="publisher" content={finalSEOProps.publisher} />

      {finalSEOProps.seoData?.metaRobots && (
        <meta
          name="robots"
          content={String(finalSEOProps.seoData.metaRobots)}
        />
      )}
      {finalSEOProps.seoData?.metaViewport && (
        <meta
          name="viewport"
          content={String(finalSEOProps.seoData.metaViewport)}
        />
      )}

      {/* Use backend-generated OpenGraph data if available, otherwise fallback to manual tags */}
      {currentProduct.seo?.openGraph ? (
        // Backend-generated OpenGraph data (preferred) - replaces database values
        Object.entries(currentProduct.seo.openGraph).map(
          ([property, content]) => (
            <meta
              key={property}
              property={property}
              content={String(content)}
            />
          )
        )
      ) : (
        // Fallback manual tags (legacy support for products without generated OpenGraph)
        <>
          <meta property="product:price:amount" content={String(finalPrice)} />
          <meta property="product:price:currency" content="PEN" />
          <meta
            property="product:availability"
            content={stockNumber > 0 ? "in stock" : "out of stock"}
          />
          <meta property="product:condition" content="new" />
          <meta property="product:retailer_item_id" content={productSku} />
          <meta property="product:brand" content={partnerName} />
          <meta
            property="product:item_group_id"
            content={currentProduct.id?.toString() || productSku}
          />

          {hasDiscount && (
            <>
              <meta
                property="product:sale_price:amount"
                content={String(discountPrice)}
              />
              <meta property="product:sale_price:currency" content="PEN" />
              {discountEndDate && (
                <meta
                  property="product:sale_price_effective_date"
                  content={`${new Date().toISOString().split("T")[0]}/${
                    discountEndDate.split("T")[0]
                  }`}
                />
              )}
            </>
          )}
        </>
      )}

      <link rel="canonical" href={canonicalURL} />
    </SEO>
  );
};

export default ProductSEO;
